import axios, { AxiosError } from "axios";
import { 
  type LoginInput, 
  type AuthResponse, 
  type RegisterRequest,
  type Room,
  type CreateRoomInput,
  type JoinRoomInput,
  type UpdateRoomSettingsInput,
} from "@argumint/shared";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // This includes cookies in requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor — attach Bearer token from localStorage on every request.
// This is the primary auth mechanism for cross-domain deployments (Vercel →
// Render) where third-party httpOnly cookies are blocked by modern browsers.
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — persist token whenever the server sends one (login / register).
// Also handles global 401: when any non-auth request comes back 401 while we
// have a token in localStorage, the session has been evicted server-side.
// We clear local auth state and redirect to /login with an explanation so the
// user isn't stuck on a broken screen with a cryptic error message.
apiClient.interceptors.response.use(
  (response) => {
    const data = response.data as { token?: string };
    if (data.token) {
      localStorage.setItem("token", data.token);
    }
    return response;
  },
  (error: AxiosError) => {
    const isAuthEndpoint = (error.config?.url ?? "").startsWith("/auth/");
    const had401 = error.response?.status === 401;
    // Check both token AND cached user — checkAuth() may have already cleared
    // the token by the time the user triggers another API call, so fall back
    // to argumint_user as a proxy for "there was an active session".
    const hadToken = !!localStorage.getItem("token") || !!localStorage.getItem("argumint_user");

    // Only redirect for non-auth endpoints (login/register expect 401s).
    if (had401 && hadToken && !isAuthEndpoint) {
      localStorage.removeItem("token");
      localStorage.removeItem("argumint_user");
      window.location.href = "/login?reason=session_expired";
      // Return a never-resolving promise so the caller doesn't also show an error
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

// Error handler
// Error handler — for HTTP errors (4xx/5xx) we throw a plain Error with the
// server's message but also attach .response so callers can distinguish an
// HTTP failure from a network failure (no .response = server unreachable).
// For network errors we rethrow the original AxiosError so its .code /
// .message ("Network Error") and absent .response are preserved intact.
const handleError = (error: AxiosError): never => {
  if (error.response?.data && typeof error.response.data === "object") {
    const data = error.response.data as { error?: string; message?: string };
    // Prefer the detailed `message` field (zod validation reason) over the
    // generic `error` label ("Invalid input"), so the UI shows something useful.
    const wrapped = Object.assign(
      new Error(data.message || data.error || "An error occurred"),
      { response: error.response },
    );
    throw wrapped;
  }
  throw error; // network error — preserve original AxiosError
};

export const authApi = {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>("/auth/register", data);
      return response.data;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },

  async login(data: LoginInput & { force?: boolean }): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>("/auth/login", data);
      return response.data;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },

  async getMe(): Promise<AuthResponse> {
    try {
      const response = await apiClient.get<AuthResponse>("/auth/me");
      return response.data;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },
};

export const roomApi = {
  async createRoom(data: CreateRoomInput): Promise<Room> {
    try {
      const response = await apiClient.post<Room>("/rooms/create", data);
      return response.data;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },

  async getRoomByCode(code: string): Promise<Room> {
    try {
      const response = await apiClient.get<Room>(`/rooms/${code}`);
      return response.data;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },

  async joinRoom(data: JoinRoomInput): Promise<Room> {
    try {
      const response = await apiClient.post<Room>("/rooms/join", data);
      return response.data;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },

  async updateRoomSettings(
    roomId: string,
    data: UpdateRoomSettingsInput
  ): Promise<Room> {
    try {
      const response = await apiClient.put<Room>(
        `/rooms/${roomId}/settings`,
        data
      );
      return response.data;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },
};

export const platformApi = {
  async getStats(): Promise<{ activeRooms: number; liveDebates: number; totalDebates: number }> {
    try {
      const res = await apiClient.get<{ activeRooms: number; liveDebates: number; totalDebates: number }>("/rooms/stats");
      return res.data;
    } catch {
      return { activeRooms: 0, liveDebates: 0, totalDebates: 0 };
    }
  },

  async getLeaderboard(): Promise<{ id: string; username: string; xp: number; debatesWon: number; totalDebates: number }[]> {
    try {
      const res = await apiClient.get("/auth/leaderboard");
      return res.data;
    } catch {
      return [];
    }
  },
};

export interface DebateHistoryEntry {
  id:                string;
  roomCode:          string;
  topic:             string;
  mode:              "buzzer" | "alternate";
  endedAt:           string;
  totalDebaters:     number;
  totalJudges:       number;
  rank:              number | null;
  totalParticipants: number;
  points:            number | null;
  isWinner:          boolean | null;
  side:              "for" | "against" | null;
}

export const historyApi = {
  async getHistory(): Promise<DebateHistoryEntry[]> {
    try {
      const res = await apiClient.get<DebateHistoryEntry[]>("/auth/history");
      return res.data;
    } catch {
      return [];
    }
  },
};

/** Pillar breakdown for one judged session (0–1 each; null = pillar not active yet) */
export interface JudgePillarScores {
  p1_rankAgreement:       number | null;
  p2_gapPreservation:     number | null;
  p3_consensusSimilarity: number | null;
  p4_outlierCoherence:    number | null;
  p5_biasDetection:       number | null;
  p6_integrityScore:      number | null;
}

/** One entry of GET /auth/judge-history `sessions` (newest first) */
export interface JudgeHistorySession {
  id:               string;
  debateId:         string | null;
  topic:            string | null;
  debateCreatedAt:  string | null;
  sessionScore:     number | null;
  credibilityAfter: number | null;
  pillar:           JudgePillarScores | null;
  createdAt:        string;
}

export const judgeApi = {
  async getHistory(): Promise<JudgeHistorySession[]> {
    try {
      const res = await apiClient.get<{ sessions?: JudgeHistorySession[] }>("/auth/judge-history");
      return Array.isArray(res.data?.sessions) ? res.data.sessions : [];
    } catch {
      return [];
    }
  },
};

export const debateApi = {
  /**
   * Upload an audio blob to /debates/:id/transcribe and get back the
   * Whisper transcript text. Caller is responsible for then emitting
   * `debate:submit-argument` over the socket with the returned text.
   */
  async transcribe(debateId: string, audio: Blob): Promise<string> {
    try {
      const form = new FormData();
      // Use a sensible filename so the backend hands Whisper a recognizable extension.
      const filename = audio.type.includes("ogg")
        ? "speech.ogg"
        : audio.type.includes("mp4")
        ? "speech.mp4"
        : "speech.webm";
      form.append("audio", audio, filename);
      const response = await apiClient.post<{ text: string }>(
        `/debates/${debateId}/transcribe`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.text;
    } catch (error) {
      handleError(error as AxiosError);
      throw error;
    }
  },
};
